import { useEffect, useRef } from 'react';
import { useReducedMotion } from '@/lib/useReducedMotion';

/**
 * The hero's live forecast engine.
 *
 * Draws the product's central idea rather than describing it: observed history
 * arrives tick by tick in data ink, stops at an as-of rule, and a quantile cone
 * unfurls beyond it in engine ink with a median through the middle.
 *
 * Two things make it read as a market rather than a diagram:
 *
 *  - the history is fractal (several octaves of noise), so it has the sharp
 *    reversals and small chop of a real tape instead of one smooth wave;
 *  - the forecast is a *curve*, not a wedge. The median keeps wandering past
 *    the as-of point and the band walls follow it, widening with sqrt(horizon)
 *    the way genuine forecast uncertainty grows. A straight triangle implies a
 *    confidence — and a linearity — that no forecast has.
 *
 * Deliberately unlabelled and seeded. It is structural motion, not a reading:
 * nothing here is presented as a real series, which is the same rule the
 * product applies to its own panels.
 */

/** Mulberry32 — small, fast, and deterministic so every visitor sees one shape. */
function seeded(seed: number) {
  return () => {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Observed samples. */
const HISTORY = 96;
/** Forecast samples drawn beyond the as-of rule. */
const HORIZON = 74;
/** Share of the width given to history. */
const SPLIT = 0.54;

/**
 * Fractal series: a slow trend carrying progressively finer, faster octaves.
 * The high octaves are what give it the jagged tape-like texture.
 */
function buildHistory(): number[] {
  const rand = seeded(20260810);
  const octaves = [
    { step: 0.055, weight: 1.0 },
    { step: 0.14, weight: 0.5 },
    { step: 0.36, weight: 0.26 },
    { step: 0.9, weight: 0.12 },
  ];

  const walks = octaves.map(() => 0);
  const out: number[] = [];

  for (let i = 0; i < HISTORY; i++) {
    let value = 0;
    octaves.forEach((o, k) => {
      walks[k] += (rand() - 0.5) * o.step;
      // Each octave reverts to zero at a rate proportional to its speed, so the
      // fast ones stay as chop and only the slow one carries the trend.
      walks[k] -= walks[k] * o.step * 0.5;
      value += walks[k] * o.weight;
    });
    out.push(value);
  }
  return out;
}

/**
 * Forecast median: continues the tape's last direction, then bends. Momentum
 * decays and a slow wander takes over, so the path curves instead of running
 * straight to the edge.
 */
function buildForecast(lastValue: number, lastSlope: number): number[] {
  const rand = seeded(981_733);
  const out: number[] = [];
  let value = lastValue;
  let momentum = lastSlope;

  for (let i = 0; i < HORIZON; i++) {
    const t = i / HORIZON;
    momentum *= 0.93;
    // A long, low-frequency wander plus a whisper of noise. Kept far smoother
    // than the history: this is a median, and a median does not chop.
    const wander = Math.sin(t * 3.1 + 0.9) * 0.01 + (rand() - 0.5) * 0.004;
    value += momentum + wander;
    out.push(value);
  }
  return out;
}

export function ForecastCanvas({ className }: { className?: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const reduced = useReducedMotion();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // The slope is averaged over the last few ticks: a single tick of a fractal
    // series is mostly high-octave noise, and handing that to the median sends
    // it straight off the frame.
    const raw = buildHistory();
    const tail = raw.slice(-6);
    const slope = (tail[tail.length - 1] - tail[0]) / (tail.length - 1);
    const rawForecast = buildForecast(raw[raw.length - 1], slope * 0.55);

    // History and forecast share one scale, normalised so the whole scene sits
    // inside the frame no matter what the seed produced.
    const all = [...raw, ...rawForecast];
    const lo = Math.min(...all);
    const hi = Math.max(...all);
    const span = hi - lo || 1;
    const norm = (v: number) => ((v - lo) / span) * 2 - 1;
    const history = raw.map(norm);
    const forecast = rawForecast.map(norm);

    let width = 0;
    let height = 0;
    let raf = 0;
    let start = 0;

    const pointer = { x: 0.5, y: 0.5 };
    const eased = { x: 0.5, y: 0.5 };

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = rect.width;
      height = rect.height;
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const onPointerMove = (e: PointerEvent) => {
      const rect = canvas.getBoundingClientRect();
      pointer.x = (e.clientX - rect.left) / rect.width;
      pointer.y = (e.clientY - rect.top) / rect.height;
    };

    const styles = getComputedStyle(document.documentElement);
    const read = (name: string, fallback: string) =>
      styles.getPropertyValue(name).trim() || fallback;

    const draw = (time: number) => {
      if (!start) start = time;
      const elapsed = (time - start) / 1000;

      eased.x += (pointer.x - eased.x) * 0.045;
      eased.y += (pointer.y - eased.y) * 0.045;
      const px = (eased.x - 0.5) * 26;
      const py = (eased.y - 0.5) * 18;

      const inkObserved = read('--wft-ink-0', '#e8edf4');
      const inkEngine = read('--engine', '#6ea8ff');
      const inkGold = read('--gold', '#ffb02e');
      const inkFaint = read('--hairline', 'rgba(255,255,255,0.085)');
      const light = document.documentElement.getAttribute('data-wft-theme') === 'canon';

      ctx.clearRect(0, 0, width, height);

      const padX = width * 0.06;
      const usable = width - padX * 2;
      const histW = usable * SPLIT;
      const foreW = usable * (1 - SPLIT);
      const splitX = padX + histW;
      const midY = height * 0.54;
      const amp = height * 0.2;

      // Grid.
      ctx.strokeStyle = inkFaint;
      ctx.lineWidth = 1;
      for (let i = 1; i < 5; i++) {
        const y = height * (0.22 + i * 0.14) + py * 0.3;
        ctx.beginPath();
        ctx.moveTo(padX, y);
        ctx.lineTo(width - padX, y);
        ctx.stroke();
      }

      const histX = (i: number) => padX + (i / (HISTORY - 1)) * histW + px * 0.35;
      const foreX = (i: number) => splitX + ((i + 1) / HORIZON) * foreW + px * 0.35;
      const toY = (v: number) => midY - v * amp + py * 0.6;

      // Cycle: history draws in, cone unfurls, both hold, then repeat.
      const period = 9;
      const phase = reduced ? 1 : (elapsed % period) / period;
      const histProgress = reduced ? 1 : Math.min(1, (phase / 0.42) ** 0.9);
      const coneProgress = reduced ? 1 : Math.max(0, Math.min(1, (phase - 0.4) / 0.34));

      const drawnHist = Math.max(2, Math.floor(HISTORY * histProgress));

      // ── Forecast cone ────────────────────────────────────────────────────
      // Band walls track the median and widen with sqrt(t), so the shape is a
      // curved horn rather than a wedge.
      if (coneProgress > 0) {
        const breathe = reduced ? 1 : 1 + Math.sin(elapsed * 0.85) * 0.06;
        const shown = Math.max(2, Math.floor(HORIZON * coneProgress));
        const anchorY = toY(history[HISTORY - 1]);

        const bands: Array<[number, number]> = [
          [1.0, 0.1], // q10–q90
          [0.6, 0.15], // q20–q80
          [0.28, 0.2], // q40–q60
        ];
        const k = light ? 1.8 : 1;

        for (const [spreadScale, alpha] of bands) {
          ctx.beginPath();
          ctx.moveTo(splitX + px * 0.35, anchorY);

          // Upper wall out…
          for (let i = 0; i < shown; i++) {
            const t = (i + 1) / HORIZON;
            const w = Math.sqrt(t) * height * 0.13 * spreadScale * breathe;
            ctx.lineTo(foreX(i), toY(forecast[i]) - w);
          }
          // …and the lower wall back.
          for (let i = shown - 1; i >= 0; i--) {
            const t = (i + 1) / HORIZON;
            const w = Math.sqrt(t) * height * 0.13 * spreadScale * breathe;
            ctx.lineTo(foreX(i), toY(forecast[i]) + w);
          }
          ctx.closePath();
          ctx.fillStyle = withAlpha(inkEngine, alpha * k);
          ctx.fill();
        }

        // Median.
        ctx.beginPath();
        ctx.moveTo(splitX + px * 0.35, anchorY);
        for (let i = 0; i < shown; i++) ctx.lineTo(foreX(i), toY(forecast[i]));
        ctx.strokeStyle = inkEngine;
        ctx.lineWidth = 2.2;
        ctx.lineJoin = 'round';
        ctx.shadowColor = withAlpha(inkEngine, 0.55);
        ctx.shadowBlur = 16;
        ctx.stroke();
        ctx.shadowBlur = 0;

        // Random-walk baseline: flat by definition, which is the point — it is
        // what the engine has to beat.
        ctx.beginPath();
        ctx.setLineDash([5, 6]);
        ctx.moveTo(splitX + px * 0.35, anchorY);
        ctx.lineTo(foreX(shown - 1), anchorY);
        ctx.strokeStyle = withAlpha(inkObserved, 0.3);
        ctx.lineWidth = 1.4;
        ctx.stroke();
        ctx.setLineDash([]);
      }

      // ── Observed history ─────────────────────────────────────────────────
      ctx.beginPath();
      for (let i = 0; i < drawnHist; i++) {
        const x = histX(i);
        const y = toY(history[i]);
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.strokeStyle = inkObserved;
      ctx.lineWidth = 1.8;
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';
      ctx.stroke();

      // Live head of the tape.
      if (drawnHist > 1 && histProgress < 1) {
        const hx = histX(drawnHist - 1);
        const hy = toY(history[drawnHist - 1]);
        const pulse = 3.2 + Math.sin(elapsed * 5) * 1.1;
        ctx.beginPath();
        ctx.arc(hx, hy, pulse, 0, Math.PI * 2);
        ctx.fillStyle = inkObserved;
        ctx.shadowColor = withAlpha(inkObserved, 0.85);
        ctx.shadowBlur = 16;
        ctx.fill();
        ctx.shadowBlur = 0;
      }

      // As-of rule.
      ctx.beginPath();
      ctx.moveTo(splitX + px * 0.35, height * 0.14 + py * 0.3);
      ctx.lineTo(splitX + px * 0.35, height * 0.88 + py * 0.3);
      ctx.strokeStyle = withAlpha(inkGold, 0.5);
      ctx.lineWidth = 1.4;
      ctx.stroke();

      if (!reduced) raf = requestAnimationFrame(draw);
    };

    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(canvas);
    window.addEventListener('pointermove', onPointerMove, { passive: true });

    if (reduced) draw(0);
    else raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
      window.removeEventListener('pointermove', onPointerMove);
    };
  }, [reduced]);

  return <canvas ref={canvasRef} className={className} aria-hidden="true" />;
}

/** Accepts #rrggbb or an rgb()/rgba() string and returns rgba() at `alpha`. */
function withAlpha(color: string, alpha: number): string {
  if (color.startsWith('#')) {
    const hex = color.slice(1);
    const full =
      hex.length === 3
        ? hex
            .split('')
            .map((c) => c + c)
            .join('')
        : hex;
    const r = parseInt(full.slice(0, 2), 16);
    const g = parseInt(full.slice(2, 4), 16);
    const b = parseInt(full.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  const nums = color.match(/[\d.]+/g);
  if (!nums || nums.length < 3) return color;
  return `rgba(${nums[0]}, ${nums[1]}, ${nums[2]}, ${alpha})`;
}
